import { Calendar } from "lucide-react";
import { ReportingPeriod } from "@/types/ReportingPeriod";
import { PeriodNode } from "./PeriodNode";
import { RefObject } from "react";
import { CreateReportingPeriod } from "../../CreateReportingPeriodDialog/CreateReportingPeriod";

interface TimelineContainerProps {
  timelineRef: React.RefObject<HTMLDivElement | null>;
  periodsWithSpacing: (ReportingPeriod & { daysSincePrevious: number })[];
  selectedReportingPeriod?: ReportingPeriod;
  onSelectPeriod: (period: ReportingPeriod) => void;
  onCreatePeriod?: (values: { period: Date; isOpen: boolean }) => Promise<void>;
  toggleStatus: (period: ReportingPeriod) => void;
}

export function TimelineContainer({
  timelineRef,
  periodsWithSpacing,
  selectedReportingPeriod,
  onSelectPeriod,
  onCreatePeriod,
  toggleStatus,
}: TimelineContainerProps) {
  return (
    <div className="w-full border-b border-border bg-background/95 backdrop-blur-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded bg-primary/10">
            <Calendar className="h-4 w-4 text-primary" />
          </div>
          <span className="text-sm font-medium">Reporting Timeline</span>
          <span className="px-2 py-0.5 bg-muted rounded-full text-xs text-muted-foreground">
            {periodsWithSpacing.length} periods
          </span>
        </div>
        {onCreatePeriod && <CreateReportingPeriod onSubmit={onCreatePeriod} />}
      </div>

      {/* Timeline */}
      <div
        ref={timelineRef}
        className="relative overflow-x-auto hide-scrollbar py-4"
        style={{ scrollbarWidth: "none" }}
        onLoad={(e) => {
          if (e.currentTarget) {
            e.currentTarget.scrollLeft = e.currentTarget.scrollWidth;
          }
        }}
      >
        {/* Timeline track */}
        <div className="absolute top-[32px] left-6 right-6 h-0.5 bg-gradient-to-r from-transparent via-primary/50 to-transparent z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-primary to-primary/20 animate-pulse" />
        </div>

        {/* Timeline periods */}
        <div className="flex items-center px-6 min-w-max">
          {periodsWithSpacing.map((period, index) => (
            <div key={period.reportingPeriodId} className="flex items-center">
              {index > 0 && (
                <div className="relative flex-shrink-0">
                  <div
                    style={{
                      width: `${
                        Math.min(
                          Math.max(period.daysSincePrevious * 2, 48),
                          200
                        ) - 12
                      }px`,
                    }}
                    className="h-px"
                  />
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                    <div className="px-2 py-0.5 bg-background/90 backdrop-blur-sm border border-border/50 rounded-full">
                      <span className="text-xs text-muted-foreground font-medium whitespace-nowrap">
                        {period.daysSincePrevious}d
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <PeriodNode
                period={period}
                toggleStatus={toggleStatus}
                isSelected={
                  selectedReportingPeriod?.reportingPeriodId ===
                  period.reportingPeriodId
                }
                index={index}
                onSelect={onSelectPeriod}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
